import { inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, EMPTY, switchMap, throwError } from 'rxjs';

import {
  extractErrorMessages,
  getFallbackErrorMessage,
  shouldSuppressErrorBody,
} from './http-error.utils';
import { CORE_AUTH_ENDPOINTS } from '../models';
import { AuthenticationService } from '../services';
import { PROPAGATE_404 } from '../tokens/http-context.tokens';
import { NttToastService } from '@ui/alert';

const REFRESH_ENDPOINT = CORE_AUTH_ENDPOINTS.refreshToken;

function carriesCaptchaToken(request: HttpRequest<unknown>): boolean {
  const data = (request.body as { data?: { reCAPTCHA?: unknown } } | null)
    ?.data;
  return typeof data?.reCAPTCHA === 'string';
}

/**
 * Surfaces the API's own message (e.g. "نرخ تبدیل به ریال یافت نشد.") and only
 * falls back to a generic Persian string when the body carries nothing usable.
 */
function notifyError(toast: NttToastService, error: HttpErrorResponse): void {
  if (shouldSuppressErrorBody(error.status)) {
    toast.showError(getFallbackErrorMessage(error.status));
    return;
  }

  const messages = extractErrorMessages(error.error);
  const toasts =
    messages.length > 0 ? messages : [getFallbackErrorMessage(error.status)];
  toasts.forEach((message) => toast.showError(message));
}

export const httpErrorInterceptor: HttpInterceptorFn = (request, next) => {
  const toast = inject(NttToastService);
  const authService = inject(AuthenticationService);
  const router = inject(Router);

  const invalidateSession = (): typeof EMPTY => {
    authService.clearTokens();
    void router.navigate(['/auth/signin'], {
      queryParams: authService.getReturnUrlQueryParam(router.url),
    });
    return EMPTY;
  };

  const isSessionEvent = (error: HttpErrorResponse): boolean =>
    error.status === 401 &&
    (request.headers.has('Authorization') ||
      authService.snapshot().isAuthenticated);

  const isCaptchaProtected = carriesCaptchaToken(request);

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        if (!isSessionEvent(error)) {
          notifyError(toast, error);
          return throwError(() => error);
        }

        if (request.url.includes(REFRESH_ENDPOINT)) {
          return invalidateSession();
        }

        if (!authService.getPersistFlag()) {
          return invalidateSession();
        }

        return authService.refreshTokens().pipe(
          switchMap((newTokens) => {
            if (!newTokens?.accessToken) {
              return invalidateSession();
            }

            if (isCaptchaProtected) {
              notifyError(toast, error);
              return throwError(() => error);
            }

            const retryRequest = request.clone({
              setHeaders: {
                Authorization: authService.authHeader(newTokens),
              },
            });

            return next(retryRequest).pipe(
              catchError((retryError: HttpErrorResponse) => {
                if (retryError.status === 401) {
                  return invalidateSession();
                }

                notifyError(toast, retryError);
                return throwError(() => retryError);
              }),
            );
          }),
        );
      }

      if (error.status === 404) {
        notifyError(toast, error);
        if (request.context.get(PROPAGATE_404)) {
          return throwError(() => error);
        }
        return EMPTY;
      }

      notifyError(toast, error);

      return throwError(() => error);
    }),
  );
};
